import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js"
import { useMutation } from "@tanstack/react-query"
import { createFileRoute, redirect } from "@tanstack/react-router"
import {
  CheckoutFailReason,
  PaymentFlow,
  ReserveSessionStatus,
  ReserveSetupIntentFailReason,
} from "ipctg-inflow-types/InFlowEnums"
import { FormEvent, useState } from "react"
import { Trans, useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import Checkbox from "@/globalComponents/atoms/Checkbox"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Separator from "@/globalComponents/atoms/Separator"
import Text from "@/globalComponents/atoms/Text"
import DueTodaySummary from "@/globalComponents/molecules/DueTodaySummary"
import StripeElementsWrapper from "@/globalComponents/molecules/StripeElementsWrapper"
import {
  ExpectedError,
  ReservePaymentIntentError,
  ReserveSessionStatusError,
  ReserveSetupIntentError,
} from "@/utilities/expectedErrors"
import {
  reservePaymentIntentQueryOptions,
  reserveSetupIntentQueryOptions,
} from "@/utilities/hooks/queries/reserve"
import { useNotice } from "@/utilities/hooks/useNotice"
import { useReservationHold } from "@/utilities/hooks/useReservationHold"
import { useStripePaymentElement } from "@/utilities/hooks/useStripePaymentElement"
import { log } from "@/utilities/logging"
import { prettyCurrencyNumber } from "@/utilities/normalizers"
import {
  isConcurrentSessionSentinel,
  isHoldExpired,
  isReserveTimeoutError,
  redirectToReserveTimeout,
  RESERVE_CONCURRENT_SESSION_ROUTE,
} from "@/utilities/reserveHold"

import styles from "./checkout.module.css"

export const Route = createFileRoute(
  "/qr/$qrLookupID/reserve/$epoch/calendars/$calendarID/dates/$year/$month/$day/checkout"
)({
  staticData: {
    pageName: "Checkout",
  },
  beforeLoad: async ({ context, params }) => {
    // The parent reserve route already fetched /session-status, and its
    // transactionDetails tell us which Stripe flow to run. Reading it from
    // there avoids a second POST /reserve/checkout, which rebuilds the
    // transaction from the session and drops any promo applied in the cart.
    const {
      sessionState: { transactionDetails },
    } = context

    try {
      const intent =
        transactionDetails?.paymentFlow === PaymentFlow.PaymentIntent
          ? {
              flow: PaymentFlow.PaymentIntent as const,
              ...(await context.queryClient.fetchQuery(
                reservePaymentIntentQueryOptions()
              )),
            }
          : {
              flow: PaymentFlow.SetupIntent as const,
              ...(await context.queryClient.fetchQuery(
                reserveSetupIntentQueryOptions()
              )),
            }

      /*
        A free rate comes back with requiresPayment false and a null
        clientSecret, so this has to run before the clientSecret guard below or
        the "nothing to pay" case reads as a system error.
      */
      if (
        intent.flow === PaymentFlow.PaymentIntent &&
        !intent.requiresPayment
      ) {
        throw redirect({
          to: "/qr/$qrLookupID/reserve/$epoch/calendars/$calendarID/dates/$year/$month/$day/confirmation",
          params,
        })
      }

      if (!intent.clientSecret) {
        const error =
          intent.flow === PaymentFlow.PaymentIntent
            ? new ReservePaymentIntentError(CheckoutFailReason.SystemError)
            : new ReserveSetupIntentError(
                ReserveSetupIntentFailReason.SystemError
              )
        throw error
      }

      return {
        clientSecret: intent.clientSecret,
        customerSessionClientSecret: intent.customerSessionClientSecret,
      }
    } catch (error: unknown) {
      if (
        error instanceof ExpectedError &&
        error.failReason === CheckoutFailReason.PaymentAlreadyCompleted
      ) {
        throw redirect({
          to: "/qr/$qrLookupID/reserve/$epoch/payment-complete",
          params,
        })
      }
      if (isReserveTimeoutError(error)) {
        redirectToReserveTimeout(params)
      }
      throw error
    }
  },
  component: RouteComponent,
})

function RouteComponent() {
  const navigate = Route.useNavigate()
  const {
    clientSecret,
    customerSessionClientSecret,
    sessionState: { transactionDetails },
  } = Route.useRouteContext()

  const { t } = useTranslation(["reserve"])

  return (
    <StripeElementsWrapper
      clientSecret={clientSecret}
      customerSessionClientSecret={customerSessionClientSecret || undefined}
      flow="reserve"
    >
      <Card>
        <Heading level={1} as={2}>
          {t("cart.paymentDetails", { ns: "reserve" })}
        </Heading>
        {transactionDetails?.reservationNoShowFeeConfig && (
          <Text level={2} style={{ marginTop: "var(--space-2)" }}>
            {/*
              A SetupIntent means nothing is charged today and the card is only
              held against a no-show. A PaymentIntent means the parker is paying
              now, so the "free spots" framing doesn't apply.
            */}
            {t(
              transactionDetails.paymentFlow === PaymentFlow.SetupIntent
                ? "cart.paymentDetailsNoShowDescription"
                : "cart.paymentDetailsNoShowDescriptionPaid",
              { ns: "reserve" }
            )}
          </Text>
        )}
        <CheckoutForm
          goNext={() =>
            navigate({
              to: "/qr/$qrLookupID/reserve/$epoch/calendars/$calendarID/dates/$year/$month/$day/confirmation",
            })
          }
        />
      </Card>
    </StripeElementsWrapper>
  )
}

interface CheckoutFormProps {
  goNext: () => Promise<void>
}

function CheckoutForm(props: CheckoutFormProps) {
  const {
    sessionState: { transactionDetails },
  } = Route.useRouteContext()
  const navigate = Route.useNavigate()
  const { qrLookupID, epoch } = Route.useParams()

  const stripe = useStripe()
  const elements = useElements()
  const { checkExpiry, expireHold, setIsConfirmingPayment } =
    useReservationHold()
  const { t } = useTranslation(["common", "errors", "reserve"])
  const [error, setError] = useState<Error>()
  const [sessionError, setSessionError] = useState<Error>()
  const [agreedToNoShowFee, setAgreedToNoShowFee] = useState(false)
  const [noShowFeeError, setNoShowFeeError] = useState(false)
  /* A deposit rate has to explain the estimated total before the parker pays. */
  const depositNotice = useNotice({
    id: "reservation-deposit",
    schedule: { mode: "once" },
  })
  const [isDepositBlockingSubmit, setIsDepositBlockingSubmit] = useState(false)
  const [authorizedCard, setAuthorizedCard] = useState(false)
  const [cardAuthorizationError, setCardAuthorizationError] = useState(false)

  const confirmMethod =
    transactionDetails?.paymentFlow === PaymentFlow.SetupIntent
      ? "confirmSetup"
      : "confirmPayment"

  const { paymentElementProps, stripeError } = useStripePaymentElement({
    flow: "reserve",
  })

  const { mutate: confirmReservation, isPending } = useMutation({
    mutationFn: confirmReservationMutationFn,
  })

  if (stripeError) throw stripeError

  if (sessionError) throw sessionError

  const reservationNoShowFeeConfig =
    transactionDetails?.reservationNoShowFeeConfig
  /*
    A deposit leaves a balance to settle at exit, so the parker authorizes the
    card for more than today's amount before it can be charged at all.
  */
  const reservationDepositConfig = transactionDetails?.reservationDepositConfig

  return (
    <form onSubmit={handleSubmit}>
      <fieldset disabled={!stripe || !elements || isPending}>
        <PaymentElement
          {...paymentElementProps}
          options={{ terms: { card: "never" } }}
        />
        <Separator />
        <DueTodaySummary
          amountDue={transactionDetails?.amountDue || 0}
          expectedAmountDue={transactionDetails?.expectedAmountDue}
          notice={depositNotice}
          isBlockingSubmit={isDepositBlockingSubmit}
          onBlockingSubmitResolve={onDepositNoticeResolve}
        />
        {reservationNoShowFeeConfig && (
          // The box and its error are one fieldset grid item, so the fieldset's
          // gap doesn't push the error away from the checkbox it belongs to.
          <div className={styles.agreementSection}>
            <div
              className={styles.agreement}
              data-invalid={noShowFeeError || undefined}
            >
              <Checkbox
                className={styles.checkboxField}
                value={agreedToNoShowFee}
                isInvalid={noShowFeeError}
                onChange={(isSelected) => {
                  setAgreedToNoShowFee(isSelected)
                  if (isSelected) setNoShowFeeError(false)
                }}
              >
                <div style={{ marginTop: "2px" }}>
                  <Trans
                    i18nKey="cart.noShowFeeAgreement"
                    ns="reserve"
                    values={{
                      amount: prettyCurrencyNumber(
                        reservationNoShowFeeConfig.amount
                      ),
                    }}
                    components={{ 1: <strong /> }}
                  />
                </div>
              </Checkbox>
            </div>
            {noShowFeeError && (
              <FieldError>
                {t("cart.noShowFeeAgreementRequired", { ns: "reserve" })}
              </FieldError>
            )}
          </div>
        )}
        {reservationDepositConfig && (
          <div className={styles.agreementSection}>
            <div
              className={styles.agreement}
              data-invalid={cardAuthorizationError || undefined}
            >
              <Checkbox
                className={styles.checkboxField}
                value={authorizedCard}
                isInvalid={cardAuthorizationError}
                onChange={(isSelected) => {
                  setAuthorizedCard(isSelected)
                  if (isSelected) setCardAuthorizationError(false)
                }}
              >
                <div style={{ marginTop: "2px" }}>
                  {t("cart.cardAuthorizationAgreement", { ns: "reserve" })}
                </div>
              </Checkbox>
            </div>
            {cardAuthorizationError && (
              <FieldError>
                {t("cart.cardAuthorizationRequired", { ns: "reserve" })}
              </FieldError>
            )}
          </div>
        )}
        {error && (
          <FieldError>
            {error instanceof ExpectedError
              ? t(error.translationKey, { ns: "errors" })
              : error.message}
          </FieldError>
        )}
        <Button type="submit" buttonType="primary" isPending={isPending}>
          {t("cart.confirmReservation", { ns: "reserve" })}
        </Button>
      </fieldset>
    </form>
  )

  async function checkSessionBeforeConfirm(): Promise<boolean> {
    let sessionState
    try {
      sessionState = await api.reserve.sessionStatus.POST({})
    } catch (e: unknown) {
      log({
        level: "error",
        flow: "reserve",
        scope: "guard.sessionState",
        message: "Session status check before payment submit failed",
        meta: { error: e },
        sentry: "breadcrumb",
      })
      setError(new Error(t("common.somethingWentWrong", { ns: "errors" })))
      return true
    }

    if ("failReason" in sessionState) {
      setSessionError(new ReserveSessionStatusError(sessionState.failReason))
      return true
    }

    if (sessionState.status === ReserveSessionStatus.Complete) {
      log({
        level: "warn",
        flow: "reserve",
        scope: "guard.sessionState",
        message:
          "Session already complete at payment submit, redirecting to payment-complete",
      })
      navigate({
        to: "/qr/$qrLookupID/reserve/$epoch/payment-complete",
        params: { qrLookupID, epoch },
      })
      return true
    }

    if (isConcurrentSessionSentinel(sessionState.holdReleaseTimestamp)) {
      log({
        level: "warn",
        flow: "reserve",
        scope: "guard.hold",
        message:
          "Another purchase session is open at payment submit, blocking confirm",
        meta: { holdReleaseTimestamp: sessionState.holdReleaseTimestamp },
      })
      navigate({ to: RESERVE_CONCURRENT_SESSION_ROUTE })
      return true
    }

    if (isHoldExpired(sessionState.holdReleaseTimestamp)) {
      log({
        level: "warn",
        flow: "reserve",
        scope: "guard.hold",
        message:
          "Hold released server-side at payment submit, blocking confirm",
        meta: { holdReleaseTimestamp: sessionState.holdReleaseTimestamp },
      })
      expireHold()
      return true
    }

    return false
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (reservationNoShowFeeConfig && !agreedToNoShowFee) {
      setNoShowFeeError(true)
      return
    }

    if (reservationDepositConfig && !authorizedCard) {
      setCardAuthorizationError(true)
      return
    }

    if (
      depositNotice.show &&
      typeof transactionDetails?.expectedAmountDue === "number"
    ) {
      setIsDepositBlockingSubmit(true)
      return
    }

    confirmPayment()
  }

  function onDepositNoticeResolve(didContinue: boolean) {
    setIsDepositBlockingSubmit(false)
    depositNotice.acknowledge()
    if (didContinue) void confirmPayment()
  }

  async function confirmPayment() {
    /*
      The parker can sit on this page past the hold release, and the ticking
      state could be a render behind, so re-read the clock here rather than
      confirming an intent the server is about to reject.
    */
    if (checkExpiry()) {
      log({
        level: "warn",
        flow: "reserve",
        scope: "guard.hold",
        message: "Reservation expired at payment submit, blocking confirm",
      })
      expireHold()
      return
    }

    setError(undefined)
    confirmReservation()
  }

  async function confirmReservationMutationFn() {
    const isSessionStale = await checkSessionBeforeConfirm()
    if (isSessionStale) return

    if (!stripe || !elements) return

    const paymentElement = elements.getElement(PaymentElement)
    if (!paymentElement) {
      setError(new Error(t("common.paymentDetailsNotFound", { ns: "errors" })))
      return
    }

    /*
      From here the card may get charged, so the hold guard has to stand down
      until we know it didn't — see setIsConfirmingPayment.
    */
    setIsConfirmingPayment(true)

    try {
      const confirmOptions = { elements, redirect: "if_required" } as const
      const result =
        transactionDetails?.paymentFlow === PaymentFlow.SetupIntent
          ? await stripe.confirmSetup(confirmOptions)
          : await stripe.confirmPayment(confirmOptions)

      if (result.error) {
        log({
          level: "error",
          flow: "reserve",
          scope: `payment.stripe${transactionDetails?.paymentFlow === PaymentFlow.SetupIntent ? "ConfirmSetup" : "ConfirmPayment"}`,
          message: `Stripe ${confirmMethod} returned an error`,
          meta: {
            code: result.error.code,
            type: result.error.type,
            declineCode: result.error.decline_code,
          },
          sentry: "exception",
          exception: result.error,
        })
        setError(new Error(result.error.message))
        setIsConfirmingPayment(false)
        return
      }

      await new Promise((resolve) => setTimeout(resolve, 1000))
      await props.goNext()
    } catch (e: unknown) {
      log({
        level: "error",
        flow: "reserve",
        scope: `payment.${confirmMethod}`,
        message: "Reserve payment confirmation failed",
        meta: { error: e },
        sentry: e instanceof ExpectedError ? "breadcrumb" : "exception",
        exception: e instanceof Error ? e : new Error(String(e)),
      })
      if (e instanceof Error) {
        setError(e)
      } else {
        setError(new Error(t("common.somethingWentWrong", { ns: "errors" })))
      }
      setIsConfirmingPayment(false)
    }
  }
}
